import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, throwError } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { TeamWorkloadRepository } from '../../../infrastructure/repositories/team-workload.repository';
import { TeamWorkloadEntity } from '../../../domain/entities/team-workload.entity';
import { WorkloadMemberEntity } from '../../../domain/entities/workload-member.entity';
import { TeamWorkloadState } from '../../states/team-workload.state';
import { GetTeamWorkloadQuery } from './get-team-workload.query';
import { GetTeamWorkloadResult } from './get-team-workload.result';

@Injectable({ providedIn: 'root' })
export class GetTeamWorkloadUseCase {
  private readonly _repository = inject(TeamWorkloadRepository);
  private readonly _state = inject(TeamWorkloadState);

  public execute(query: GetTeamWorkloadQuery): Observable<GetTeamWorkloadResult> {
    if (!query.silent) this._state.setLoadingWorkloadOverview(true);
    this._state.setError(null);

    return forkJoin({
      overview: this._repository.getOverviewBundle(),
      teamWorkloads: this._repository.getTeamWorkloads(query.includeClosedInvestigations),
    }).pipe(
      tap(({ overview, teamWorkloads }) => {
        // Aggregate members across teams FIRST, then use aggregated data everywhere
        const aggregatedMembers = this._aggregateMembersAcrossTeams(teamWorkloads);
        const enrichedTeamWorkloads = this._enrichTeamWorkloadsWithAggregatedMembers(teamWorkloads, aggregatedMembers);
        const enriched = (members: WorkloadMemberEntity[]) =>
          this._enrichWithInvestigations(members, aggregatedMembers);
        this._state.setSummary(overview.summary);
        this._state.setOverCapacityMembers(enriched(overview.overCapacityMembers));
        this._state.setOverdueMembers(enriched(overview.overdueMembers));
        this._state.setAtRiskMembers(enriched(overview.atRiskMembers));
        this._state.setTimeOffMembers(enriched(overview.timeOffMembers));
        this._state.setTeamWorkloads(enrichedTeamWorkloads);
        this._state.setIncludeClosedInvestigations(query.includeClosedInvestigations);
        if (!query.silent) this._state.setLoadingWorkloadOverview(false);
      }),
      map(({ overview, teamWorkloads }) => ({
        summary: overview.summary,
        overCapacityMembers: overview.overCapacityMembers,
        overdueMembers: overview.overdueMembers,
        atRiskMembers: overview.atRiskMembers,
        timeOffMembers: overview.timeOffMembers,
        teamWorkloads,
      })),
      catchError((error) => {
        this._state.setError(error?.message ?? 'Failed to load team workload');
        if (!query.silent) this._state.setLoadingWorkloadOverview(false);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Aggregates members across all teams, merging team names for members who belong to multiple teams.
   * Returns a map of userId -> aggregated member with complete teams array.
   */
  private _aggregateMembersAcrossTeams(
    teamWorkloads: TeamWorkloadEntity[],
  ): Map<string, WorkloadMemberEntity> {
    const byUserId = new Map<string, WorkloadMemberEntity>();
    for (const team of teamWorkloads) {
      for (const m of team.members) {
        const existing = byUserId.get(m.userId);
        if (!existing) {
          byUserId.set(m.userId, m);
        } else {
          // Merge teams from duplicate member occurrences (member belongs to multiple teams)
          for (const teamName of m.teams) {
            if (!existing.teams.includes(teamName)) {
              existing.teams.push(teamName);
            }
          }
        }
      }
    }
    return byUserId;
  }

  private _enrichTeamWorkloadsWithAggregatedMembers(
    teamWorkloads: TeamWorkloadEntity[],
    aggregatedMembers: Map<string, WorkloadMemberEntity>,
  ): TeamWorkloadEntity[] {
    return teamWorkloads.map((team) => {
      const enrichedTeam = { ...team };
      enrichedTeam.members = team.members.map((m) => {
        const aggregated = aggregatedMembers.get(m.userId);
        return aggregated ? { ...m, teams: aggregated.teams } : m;
      });
      return enrichedTeam;
    });
  }

  private _enrichWithInvestigations(
    kpiMembers: WorkloadMemberEntity[],
    aggregatedMembers: Map<string, WorkloadMemberEntity>,
  ): WorkloadMemberEntity[] {
    return kpiMembers.map((kpi) => {
      const full = aggregatedMembers.get(kpi.userId);
      if (!full) return kpi;
      kpi.investigations = full.investigations;
      kpi.teams = full.teams;
      kpi.overdueCount = full.overdueCount;
      kpi.atRiskCount = full.atRiskCount;
      kpi.isOnTimeOff = full.isOnTimeOff;
      kpi.isManager = full.isManager;
      kpi.role = full.role;
      kpi.closedHiddenCount = full.closedHiddenCount;
      if (full.timeOff) kpi.timeOff = full.timeOff;
      return kpi;
    });
  }
}
